import { FC, memo } from 'react';
import type { DatoUpgradeProduct, SpecsCardProps } from '@framework/api/types';
import { useTranslation as t } from '@utils/hooks';
import type {
  DatoSubscriptionUpgradeOption,
  FragileApiPart,
  FragileApiParts,
  SubscriptionProductUpgradeOption,
} from '../../types';
import { ConditionOption } from '../PdpConditionSelector/PdpConditionSelector';

type ComponentSummaryProps = {
  products?: DatoUpgradeProduct | DatoSubscriptionUpgradeOption;
  selectedUpgradeOption?: SubscriptionProductUpgradeOption;
  selectedConditionOption?: ConditionOption;
  parts: FragileApiParts;
};

type PartsDataReturn = {
  operatingSystem: null | FragileApiPart;
  cpu: null | FragileApiPart;
  gpu: null | FragileApiPart;
  memory: null | FragileApiPart;
  storage: null | FragileApiPart;
  case: null | FragileApiPart;
};

const ComponentSummary: FC<ComponentSummaryProps> = ({
  products,
  selectedUpgradeOption,
  selectedConditionOption,
  parts,
}) => {
  const SOCKET_NAME_CPU = t('prebuild_socket_cpu');
  const SOCKET_NAME_GPU = t('prebuild_socket_gpu');
  const SOCKET_NAME_MEMORY = t('prebuild_socket_memory');
  const SOCKET_NAME_STORAGE = t('prebuild_socket_storage');

  const PREBUILD_WINDOWS = t('prebuild_windows');

  // Function to get parts data from the Fragile API

  const getPartsData = (): PartsDataReturn => {
    if (!selectedUpgradeOption || !parts) {
      return {
        operatingSystem: null,
        cpu: null,
        gpu: null,
        memory: null,
        storage: null,
        case: null,
      };
    }

    // Get the parts for the selected upgrade option
    const productParts =
      parts?.parts && parts.parts[selectedUpgradeOption.name];

    if (!productParts) {
      return {
        operatingSystem: null,
        cpu: null,
        gpu: null,
        memory: null,
        storage: null,
        case: null,
      };
    }

    // Function to get the newest part based on revisionDate
    const getPart = (category: string): FragileApiPart | null => {
      if (!productParts[category] || !Array.isArray(productParts[category])) {
        return null;
      }

      // Sort by revisionDate in descending order and take the first one
      return productParts[category].sort((a, b) => {
        const dateA = new Date(a.revisionDate).getTime();
        const dateB = new Date(b.revisionDate).getTime();
        if (selectedConditionOption === ConditionOption.LIKE_NEW) {
          return dateA - dateB;
        }
        return dateB - dateA;
      })[0];
    };

    // Get the parts for each category
    return {
      operatingSystem: getPart('SOFTWARE'),
      cpu: getPart('CPU'),
      gpu: getPart('GPU'),
      memory: getPart('MEMORY'),
      storage: getPart('STORAGE'),
      case: getPart('CASES'),
    };
  };

  // Extract CMS data mapping
  const keyToLabelMapPerf: { [key: string]: string } = {
    cpu: SOCKET_NAME_CPU,
    gpu: SOCKET_NAME_GPU,
  };

  const keyToLabelMapMem: { [key: string]: string } = {
    memory: SOCKET_NAME_MEMORY,
    storage: SOCKET_NAME_STORAGE,
  };

  const perfItems = products
    ? Object.entries(keyToLabelMapPerf)
        .map(([key]) => {
          const productKey = key as keyof DatoUpgradeProduct;
          if (products) {
            if (
              productKey in products &&
              typeof products[productKey] === 'object' &&
              products[productKey] &&
              products[productKey].heading
            ) {
              const { heading } = products[productKey] as SpecsCardProps;

              return { key, heading };
            }
          }
          return null;
        })
        .filter(Boolean)
    : [];

  const memItems = products
    ? Object.entries(keyToLabelMapMem)
        .map(([key]) => {
          const productKey = key as keyof DatoUpgradeProduct;
          if (products) {
            if (
              productKey in products &&
              typeof products[productKey] === 'object' &&
              products[productKey] &&
              products[productKey].heading
            ) {
              const { heading } = products[productKey] as SpecsCardProps;

              return { key, heading };
            }
          }
          return null;
        })
        .filter(Boolean)
    : [];

  // Get parts data if we're using it
  const partsData = getPartsData();

  // If we're in flex mode or using parts data, show the flex-style layout
  return (
    <div className="space-y-4">
      {/* Operating System */}
      <div>
        <h4 className="font-bold mb-1">Operating System</h4>
        <p className="text-sm">
          {partsData.operatingSystem?.friendlyName ?? PREBUILD_WINDOWS}
        </p>
      </div>

      {/* CPU */}
      <div>
        <h4 className="font-bold mb-1 ">CPU</h4>
        <p className="text-sm">
          {(partsData.cpu?.friendlyName ??
            perfItems.find(p => p.key === 'cpu')?.heading) ||
            'Loading...'}
        </p>
      </div>

      {/* GPU */}
      <div>
        <h4 className="font-bold mb-1">GPU</h4>
        <p className="text-sm">
          {(partsData.gpu?.friendlyName ??
            perfItems.find(p => p.key === 'gpu')?.heading) ||
            'Loading...'}
        </p>
      </div>

      {/* Memory */}
      <div>
        <h4 className="font-bold mb-1">Memory</h4>
        <p className="text-sm">
          {(partsData.memory?.friendlyName ??
            memItems.find(p => p.key === 'memory')?.heading) ||
            'Loading...'}
        </p>
      </div>

      {/* Storage */}
      <div>
        <h4 className="font-bold mb-1">Storage</h4>
        <p className="text-sm">
          {(partsData.storage?.friendlyName ??
            memItems.find(p => p.key === 'storage')?.heading) ||
            'Loading...'}
        </p>
      </div>

      {/* Case */}
      <div>
        <h4 className="font-bold mb-1">Case</h4>
        <p className="text-sm">
          {partsData.case?.friendlyName ?? 'Loading...'}
        </p>
      </div>
    </div>
  );
};

export default memo(ComponentSummary);
